"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var responseHandler_1 = require("./responseHandler");
var sparqlDataProviderSettings_1 = require("./sparqlDataProviderSettings");
var BlankNodes = require("./blankNodes");
var turtle_1 = require("./turtle");
var SparqlQueryMethod;
(function (SparqlQueryMethod) {
    SparqlQueryMethod[SparqlQueryMethod["GET"] = 1] = "GET";
    SparqlQueryMethod[SparqlQueryMethod["POST"] = 2] = "POST";
})(SparqlQueryMethod = exports.SparqlQueryMethod || (exports.SparqlQueryMethod = {}));
var SparqlDataProvider = /** @class */ (function () {
    function SparqlDataProvider(options, settings) {
        if (settings === void 0) { settings = sparqlDataProviderSettings_1.OWLStatsSettings; }
        var _a = options.queryFunction, queryFunction = _a === void 0 ? queryInternal : _a;
        this.options = tslib_1.__assign({}, options, { queryFunction: queryFunction });
        this.settings = settings;
    }
    SparqlDataProvider.prototype.classTree = function () {
        var query = this.settings.defaultPrefix + this.settings.classTreeQuery;
        return this.executeSparqlQuery(query).then(responseHandler_1.getClassTree);
    };
    SparqlDataProvider.prototype.propertyInfo = function (params) {
        var ids = params.propertyIds.map(escapeIri).map(function (id) { return " ( " + id + " )"; }).join(' ');
        var query = this.settings.defaultPrefix + ("\n            SELECT ?prop ?label\n            WHERE {\n                VALUES (?prop) {" + ids + "}.\n                OPTIONAL { ?prop " + this.settings.schemaLabelProperty + " ?label. }\n            }\n        ");
        return this.executeSparqlQuery(query).then(responseHandler_1.getPropertyInfo);
    };
    SparqlDataProvider.prototype.classInfo = function (params) {
        var ids = params.classIds.map(escapeIri).map(function (id) { return " ( " + id + " )"; }).join(' ');
        var query = this.settings.defaultPrefix + ("\n            SELECT ?class ?label ?instcount\n            WHERE {\n                VALUES (?class) {" + ids + "}.\n                OPTIONAL { ?class " + this.settings.schemaLabelProperty + " ?label. }\n                BIND(\"\" as ?instcount)\n            }\n        ");
        return this.executeSparqlQuery(query).then(responseHandler_1.getClassInfo);
    };
    SparqlDataProvider.prototype.linkTypesInfo = function (params) {
        var ids = params.linkTypeIds.map(escapeIri).map(function (id) { return " ( " + id + " )"; }).join(' ');
        var query = this.settings.defaultPrefix + ("\n            SELECT ?link ?label ?instcount\n            WHERE {\n                VALUES (?link) {" + ids + "}.\n                OPTIONAL { ?link " + this.settings.schemaLabelProperty + " ?label. }\n                BIND(\"\" as ?instcount)\n            }\n        ");
        return this.executeSparqlQuery(query).then(responseHandler_1.getLinkTypes);
    };
    SparqlDataProvider.prototype.linkTypes = function () {
        var query = this.settings.defaultPrefix + ("\n            SELECT ?link ?instcount ?label\n            WHERE {\n                  " + this.settings.linkTypesPattern + "\n                  OPTIONAL { ?link " + this.settings.schemaLabelProperty + " ?label. }\n            }\n        ");
        return this.executeSparqlQuery(query).then(responseHandler_1.getLinkTypes);
    };
    SparqlDataProvider.prototype.elementInfo = function (params) {
        var _this = this;
        var blankIds = [];
        var elementIds = params.elementIds.filter(function (id) { return !BlankNodes.isEncodedBlank(id); });
        var blankNodeResponse = this.options.acceptBlankNodes
            ? BlankNodes.elementInfo(params.elementIds) : undefined;
        if (elementIds.length === 0 && this.options.acceptBlankNodes) {
            return Promise.resolve(responseHandler_1.getElementsInfo(blankNodeResponse, params.elementIds));
        }
        var ids = elementIds.map(escapeIri).map(function (id) { return " (" + id + ")"; }).join(' ');
        var _a = this.settings, defaultPrefix = _a.defaultPrefix, dataLabelProperty = _a.dataLabelProperty, elementInfoQuery = _a.elementInfoQuery, propertyConfigurations = _a.propertyConfigurations;
        var query = defaultPrefix + resolveTemplate(elementInfoQuery, { ids: ids, dataLabelProperty: dataLabelProperty, propertyConfigurations: this.formatPropertyInfo() });
        return this.executeSparqlConstruct(query)
            .then(responseHandler_1.triplesToElementBinding)
            .then(function (result) { return _this.concatWithBlankNodeResponse(result, blankNodeResponse); })
            .then(function (elementsInfo) { return responseHandler_1.getElementsInfo(elementsInfo, params.elementIds); })
            .then(function (elementModels) {
            if (_this.options.prepareImages) {
                return _this.prepareElementsImage(elementModels);
            }
            else if (_this.options.imagePropertyUris && _this.options.imagePropertyUris.length) {
                return _this.enrichedElementsInfo(elementModels, _this.options.imagePropertyUris);
            }
            else {
                return elementModels;
            }
        });
    };
    SparqlDataProvider.prototype.enrichedElementsInfo = function (elementsInfo, types) {
        var ids = Object.keys(elementsInfo).filter(function (id) { return !BlankNodes.isEncodedBlank(id); })
            .map(escapeIri).map(function (id) { return " ( " + id + " )"; }).join(' ');
        var typesString = types.map(escapeIri).map(function (id) { return " ( " + id + " )"; }).join(' ');
        var query = this.settings.defaultPrefix + ("\n            SELECT ?inst ?linkType ?image\n            WHERE {{\n                VALUES (?inst) {" + ids + "}\n                VALUES (?linkType) {" + typesString + "} \n                " + this.settings.imageQueryPattern + "\n            }}\n        ");
        return this.executeSparqlQuery(query)
            .then(function (imageResponse) { return responseHandler_1.getEnrichedElementsInfo(imageResponse, elementsInfo); })
            .catch(function (err) {
            console.error(err);
            return elementsInfo;
        });
    };
    SparqlDataProvider.prototype.prepareElementsImage = function (elementsInfo) {
        return this.options.prepareImages(elementsInfo).then(function (images) {
            for (var key in images) {
                if (images.hasOwnProperty(key) && elementsInfo[key]) {
                    elementsInfo[key].image = images[key];
                }
            }
            return elementsInfo;
        });
    };
    SparqlDataProvider.prototype.linksInfo = function (params) {
        var _this = this;
        var elementIds = params.elementIds.filter(function (id) { return !BlankNodes.isEncodedBlank(id); });
        var blankNodeResponse = this.options.acceptBlankNodes
            ? BlankNodes.linksInfo(params.elementIds) : undefined;
        if (elementIds.length === 0 && this.options.acceptBlankNodes) {
            return Promise.resolve(responseHandler_1.getLinksInfo(blankNodeResponse));
        }
        var ids = elementIds.map(escapeIri).map(function (id) { return " ( " + id + " )"; }).join(' ');
        var linksInfoQuery = resolveTemplate(this.settings.linksInfoQuery, { ids: ids, linkConfigurations: this.formatLinkLinks() });
        var query = this.settings.defaultPrefix + linksInfoQuery;
        return this.executeSparqlQuery(query)
            .then(function (result) { return _this.concatWithBlankNodeResponse(result, blankNodeResponse); })
            .then(responseHandler_1.getLinksInfo);
    };
    SparqlDataProvider.prototype.linkTypesOf = function (params) {
        var _this = this;
        if (this.options.acceptBlankNodes && BlankNodes.isEncodedBlank(params.elementId)) {
            return Promise.resolve(responseHandler_1.getLinksTypesOf(BlankNodes.linkTypesOf(params)));
        }
        var elementIri = escapeIri(params.elementId);
        // Ask for linkTypes
        var query = this.settings.defaultPrefix
            + resolveTemplate(this.settings.linkTypesOfQuery, { elementIri: elementIri, linkConfigurations: this.formatLinkTypesOf(params.elementId) });
        return this.executeSparqlQuery(query)
            .then(function (linkTypeBinding) {
            var linkTypeIds = responseHandler_1.getLinksTypeIds(linkTypeBinding);
            var requests = [];
            var navigateElementFilterOut = _this.options.acceptBlankNodes ?
                "FILTER (IsIri(?outObject) || IsBlank(?outObject))" : "FILTER IsIri(?outObject)";
            var navigateElementFilterIn = _this.options.acceptBlankNodes ?
                "FILTER (IsIri(?inObject) || IsBlank(?inObject))" : "FILTER IsIri(?inObject)";
            for (var _i = 0, linkTypeIds_1 = linkTypeIds; _i < linkTypeIds_1.length; _i++) {
                var id = linkTypeIds_1[_i];
                var q = _this.settings.defaultPrefix
                    + resolveTemplate(_this.settings.linkTypesStatisticsQuery, {
                        linkId: escapeIri(id),
                        elementIri: elementIri,
                        linkConfigurations: _this.formatLinkTypesStatistics(params.elementId, id),
                        navigateElementFilterOut: navigateElementFilterOut,
                        navigateElementFilterIn: navigateElementFilterIn,
                    });
                requests.push(_this.executeSparqlQuery(q).then(responseHandler_1.getLinkStatistics));
            }
            return Promise.all(requests);
        });
    };
    ;
    SparqlDataProvider.prototype.linkElements = function (params) {
        // for sparql we have rich filtering features and we just reuse filter.
        return this.filter({
            refElementId: params.elementId,
            refElementLinkId: params.linkId,
            linkDirection: params.direction,
            limit: params.limit,
            offset: params.offset,
            languageCode: ''
        });
    };
    SparqlDataProvider.prototype.filter = function (params) {
        var _this = this;
        if (params.limit === undefined) {
            params.limit = 100;
        }
        var blankFiltration = this.options.acceptBlankNodes
            ? BlankNodes.filter(params) : undefined;
        if (this.options.acceptBlankNodes && blankFiltration.results.bindings.length > 0) {
            return Promise.resolve(responseHandler_1.getFilteredData(blankFiltration));
        }
        if (!params.refElementId && params.refElementLinkId) {
            throw new Error("Can't execute refElementLink filter without refElement");
        }
        var refQueryPart = '';
        if (params.refElementId) {
            refQueryPart = this.createRefQueryPart({
                elementId: params.refElementId,
                linkId: params.refElementLinkId,
                direction: params.linkDirection,
            });
        }
        var elementTypePart = '';
        if (params.elementTypeId) {
            var elementTypeIri = escapeIri(params.elementTypeId);
            elementTypePart = resolveTemplate(this.settings.filterTypePattern, { elementTypeIri: elementTypeIri });
        }
        var textSearchPart = '';
        if (params.text) {
            var _a = this.settings, fullTextSearch_1 = _a.fullTextSearch, dataLabelProperty_1 = _a.dataLabelProperty;
            textSearchPart = resolveTemplate(fullTextSearch_1.queryPattern, { text: params.text, dataLabelProperty: dataLabelProperty_1 });
        }
        var _b = this.settings, defaultPrefix = _b.defaultPrefix, fullTextSearch = _b.fullTextSearch, dataLabelProperty = _b.dataLabelProperty;
        var blankNodes = this.options.acceptBlankNodes;
        var query = defaultPrefix + "\n            " + fullTextSearch.prefix + "\n\n        SELECT ?inst ?class ?label ?blankType " + (blankNodes ? BlankNodes.BLANK_NODE_QUERY_PARAMETERS : '') + "\n        WHERE {\n            {\n                SELECT DISTINCT ?inst " + (textSearchPart ? '?score' : '') + " WHERE {\n                    " + elementTypePart + "\n                    " + refQueryPart + "\n                    " + textSearchPart + "\n                    " + this.settings.filterAdditionalRestriction + "\n                    " + (this.settings.fullTextSearch.extractLabel ? sparqlExtractLabel('?inst', '?extractedLabel') : '') + "\n                }\n                " + (textSearchPart ? 'ORDER BY DESC(?score)' : '') + "\n                LIMIT " + params.limit + " OFFSET " + params.offset + "\n            }\n            " + resolveTemplate(this.settings.filterElementInfoPattern, { dataLabelProperty: dataLabelProperty }) + "\n            " + (blankNodes ? BlankNodes.BLANK_NODE_QUERY : '') + "\n        } " + (textSearchPart ? 'ORDER BY DESC(?score)' : '') + "\n        ";
        return this.executeSparqlQuery(query)
            .then(function (result) {
            if (_this.options.acceptBlankNodes) {
                return BlankNodes.updateFilterResults(result, function (blankQuery) {
                    return _this.executeSparqlQuery(blankQuery);
                });
            }
            return result;
        }).then(responseHandler_1.getFilteredData);
    };
    ;
    SparqlDataProvider.prototype.executeSparqlQuery = function (query) {
        var method = this.options.queryMethod ? this.options.queryMethod : SparqlQueryMethod.GET;
        return executeSparqlQuery(this.options.endpointUrl, query, method, this.options.queryFunction);
    };
    SparqlDataProvider.prototype.concatWithBlankNodeResponse = function (response, blankNodeResponse) {
        if (!this.options.acceptBlankNodes) {
            return response;
        }
        return {
            head: { vars: response.head.vars },
            results: {
                bindings: blankNodeResponse.results.bindings.concat(response.results.bindings)
            },
        };
    };
    SparqlDataProvider.prototype.executeSparqlConstruct = function (query) {
        var method = this.options.queryMethod ? this.options.queryMethod : SparqlQueryMethod.GET;
        return executeSparqlConstruct(this.options.endpointUrl, query, method, this.options.queryFunction);
    };
    SparqlDataProvider.prototype.createRefQueryPart = function (params) {
        var elementId = params.elementId, linkId = params.linkId, direction = params.direction;
        var refElementIRI = escapeIri(params.elementId);
        // If no link configuration is passed, use rdf predicates as links
        if (this.settings.linkConfigurations.length === 0) {
            var linkPattern = linkId ? escapeIri(params.linkId) : '?link';
            var blankFilter = this.options.acceptBlankNodes
                ? 'FILTER(isIri(?inst) || isBlank(?inst))'
                : 'FILTER(isIri(?inst))';
            // link to element with specified link type
            // if direction is not specified, provide both patterns and union them
            // FILTER ISIRI is used to prevent blank nodes appearing in results
            var part = '';
            if (params.direction !== 'in') {
                part += "{ " + refElementIRI + " " + linkPattern + " ?inst . " + blankFilter + " }";
            }
            if (!params.direction) {
                part += ' UNION ';
            }
            if (params.direction !== 'out') {
                part += "{ ?inst " + linkPattern + " " + refElementIRI + " . " + blankFilter + " }";
            }
            if (this.settings.filterRefElementLinkPattern.length && !linkId) {
                part += "\n" + this.settings.filterRefElementLinkPattern;
            }
            return part;
        }
        else {
            // use link configuration in filter. If you need more or somehow mix it with rdf predicates, override
            // this function and provide nessesary sparql for this.
            var linkConfigurations = this.formatLinkElements(params.elementId, params.linkId, params.direction);
            return linkConfigurations;
        }
    };
    SparqlDataProvider.prototype.formatLinkTypesOf = function (elementIri) {
        var _this = this;
        var elementIriConst = escapeIri(elementIri);
        return this.settings.linkConfigurations.map(function (linkConfig) {
            var links = [];
            links.push("{ " + _this.formatLinkPath(linkConfig.path, elementIriConst, '?outObject') + " \n                BIND(<" + linkConfig.id + "> as ?link )\n            }");
            links.push("{ " + _this.formatLinkPath(linkConfig.path, '?inObject', elementIriConst) + " \n                BIND(<" + linkConfig.id + "> as ?link )\n            }");
            if (linkConfig.inverseId) {
                links.push("{ " + _this.formatLinkPath(linkConfig.path, elementIriConst, '?inObject') + " \n                BIND(<" + linkConfig.inverseId + "> as ?link )\n            }");
                links.push("{ " + _this.formatLinkPath(linkConfig.path, '?outObject', elementIriConst) + " \n                BIND(<" + linkConfig.inverseId + "> as ?link )\n            }");
            }
            return links;
        }).map(function (links) { return links.join("\n            UNION \n            "); }).join("\n            UNION \n            ");
    };
    SparqlDataProvider.prototype.formatLinkTypesStatistics = function (elementIri, linkIri) {
        var _this = this;
        var elementIriConst = escapeIri(elementIri);
        var links = [];
        this.settings.linkConfigurations.filter(function (link) { return link.id === linkIri; }).forEach(function (link) {
            links.push("{ " + _this.formatLinkPath(link.path, elementIriConst, '?outObject') + "\n                BIND(<" + linkIri + "> as ?link)\n            }");
            links.push("{ " + _this.formatLinkPath(link.path, '?inObject', elementIriConst) + "\n                BIND(<" + linkIri + "> as ?link)\n            }");
        });
        this.settings.linkConfigurations.filter(function (link) { return link.inverseId === linkIri; }).forEach(function (link) {
            links.push("{ " + _this.formatLinkPath(link.path, elementIriConst, '?inObject') + "\n                BIND(<" + linkIri + "> as ?link)\n            }");
            links.push("{ " + _this.formatLinkPath(link.path, '?outObject', elementIriConst) + "\n                BIND(<" + linkIri + "> as ?link)\n            }");
        });
        return links.join(" \n UNION \n ");
    };
    SparqlDataProvider.prototype.formatLinkElements = function (refElementIri, linkIri, direction) {
        var _this = this;
        var linkConfigurations = this.settings.linkConfigurations;
        var elementIriConst = "<" + refElementIri + ">";
        var parts = [];
        if (!linkIri) {
            if (!direction || direction === 'out') {
                parts = parts.concat(linkConfigurations.map(function (link) {
                    return "{ " + _this.formatLinkPath(link.path, elementIriConst, '?inst') + " }";
                }));
            }
            if (!direction || direction === 'in') {
                parts = parts.concat(linkConfigurations.map(function (link) {
                    return "{ " + _this.formatLinkPath(link.path, '?inst', elementIriConst) + " }";
                }));
            }
        }
        else {
            var outLinks = linkConfigurations.filter(function (link) { return link.id === linkIri; });
            var inLinks = linkConfigurations.filter(function (link) { return link.inverseId === linkIri; });
            if (!direction || direction === 'out') {
                parts = parts.concat(outLinks.map(function (link) { return "{ " + _this.formatLinkPath(link.path, elementIriConst, '?inst') + " }"; }), inLinks.map(function (link) { return "{ " + _this.formatLinkPath(link.path, '?inst', elementIriConst) + " }"; }));
            }
            if (!direction || direction === 'in') {
                parts = parts.concat(inLinks.map(function (link) { return "{ " + _this.formatLinkPath(link.path, elementIriConst, '?inst') + " }"; }), outLinks.map(function (link) { return "{ " + _this.formatLinkPath(link.path, '?inst', elementIriConst) + " }"; }));
            }
        }
        return parts.join(" \n UNION \n ");
    };
    SparqlDataProvider.prototype.formatLinkLinks = function () {
        var _this = this;
        return this.settings.linkConfigurations.map(function (linkConfig) {
            return "{ " + _this.formatLinkPath(linkConfig.path, '?source', '?target') + " \n                BIND(<" + linkConfig.id + "> as ?type )\n               " + (linkConfig.properties ? _this.formatLinkPath(linkConfig.properties, '?source', '?target') : '') + "\n            }";
        }).join("\n            UNION \n            ");
    };
    SparqlDataProvider.prototype.formatLinkPath = function (path, source, target) {
        return path.replace(/\$source/g, source).replace(/\$target/g, target);
    };
    SparqlDataProvider.prototype.formatPropertyInfo = function () {
        var _this = this;
        return this.settings.propertyConfigurations.map(function (propConfig) {
            return "{ " + _this.formatPropertyPath(propConfig.path, '?inst', '?propValue') + " \n                BIND(<" + propConfig.id + "> as ?propType )\n            }";
        }).join("\n            UNION \n            ");
    };
    SparqlDataProvider.prototype.formatPropertyPath = function (path, subject, value) {
        return path.replace(/\$subject/g, subject).replace(/\$value/g, value);
    };
    return SparqlDataProvider;
}());
exports.SparqlDataProvider = SparqlDataProvider;
function resolveTemplate(template, values) {
    var result = template;
    for (var replaceKey in values) {
        if (!values.hasOwnProperty(replaceKey)) {
            continue;
        }
        var replaceValue = values[replaceKey];
        result = result.replace(new RegExp('\\${' + replaceKey + '}', 'g'), replaceValue);
    }
    return result;
}
function executeSparqlQuery(endpoint, query, method, queryFunction) {
    var internalQuery;
    if (method === SparqlQueryMethod.GET) {
        internalQuery = queryFunction({
            url: appendQueryParams(endpoint, { query: query }),
            headers: {
                'Accept': 'application/sparql-results+json',
            },
            method: 'GET',
        });
    }
    else {
        internalQuery = queryFunction({
            url: endpoint,
            body: query,
            headers: {
                'Accept': 'application/sparql-results+json',
                'Content-Type': 'application/sparql-query',
            },
            method: 'POST',
        });
    }
    return internalQuery.then(function (response) {
        if (response.ok) {
            return response.json();
        }
        else {
            var error = new Error(response.statusText);
            error.response = response;
            throw error;
        }
    });
}
exports.executeSparqlQuery = executeSparqlQuery;
function executeSparqlConstruct(endpoint, query, method, queryFunction) {
    var internalQuery;
    if (method === SparqlQueryMethod.GET) {
        internalQuery = queryFunction({
            url: appendQueryParams(endpoint, { query: query }),
            headers: {
                'Accept': 'text/turtle',
            },
            method: 'GET',
        });
    }
    else {
        internalQuery = queryFunction({
            url: endpoint,
            body: query,
            headers: {
                'Accept': 'text/turtle',
                'Content-Type': 'application/sparql-query',
            },
            method: 'POST',
        });
    }
    return internalQuery.then(function (response) {
        if (response.ok) {
            return response.text();
        }
        else {
            var error = new Error(response.statusText);
            error.response = response;
            throw error;
        }
    }).then(turtle_1.parseTurtleText);
}
exports.executeSparqlConstruct = executeSparqlConstruct;
function appendQueryParams(endpoint, queryParams) {
    if (queryParams === void 0) { queryParams = {}; }
    var initialSeparator = endpoint.indexOf('?') < 0 ? '?' : '&';
    var additionalParams = initialSeparator + Object.keys(queryParams)
        .map(function (key) { return key + "=" + encodeURIComponent(queryParams[key]); })
        .join('&');
    return endpoint + additionalParams;
}
function queryInternal(params) {
    return fetch(params.url, {
        method: params.method,
        body: params.body,
        credentials: 'same-origin',
        mode: 'cors',
        cache: 'default',
        headers: params.headers,
    });
}
function sparqlExtractLabel(subject, label) {
    return "\n        BIND ( str( " + subject + " ) as ?uriStr)\n        BIND ( strafter(?uriStr, \"#\") as ?label3)\n        BIND ( strafter(strafter(?uriStr, \"//\"), \"/\") as ?label6) \n        BIND ( strafter(?label6, \"/\") as ?label5)   \n        BIND ( strafter(?label5, \"/\") as ?label4)   \n        BIND (if (?label3 != \"\", ?label3, \n            if (?label4 != \"\", ?label4, \n            if (?label5 != \"\", ?label5, ?label6))) as " + label + ")\n    ";
}
function escapeIri(iri) {
    if (typeof iri !== 'string') {
        throw new Error("Cannot escape IRI of type \"" + typeof iri + "\"");
    }
    return "<" + iri + ">";
}
exports.default = SparqlDataProvider;
